'use client';

import { useRef, useState } from 'react';
import { RotateCw } from 'lucide-react';

import { useMuxHls } from '../hooks/use-mux-hls';

/**
 * MuxHlsPlayer — a self-contained `<video>` that plays a signed Mux HLS stream
 * (BAL-721 / R3). Thin wrapper over {@link useMuxHls}; used by click-to-play
 * tiles and the media-picker cluster. The rich auto-play reel drives its own
 * `<video>` with the hook directly, so it keeps its intersection/mute behaviour.
 *
 * On a token/HLS failure it renders a graceful fallback with a Retry affordance
 * (R4 — never a silent broken player). Retry remounts the inner player via a
 * changing `key`, so the token fetch + HLS attach run again from a clean
 * `failed=false` state (no synchronous setState inside the effect).
 */
export interface MuxHlsPlayerProps {
  /** The artist_media row id — the token endpoint authorizes + mints by this. */
  mediaId: string;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  playsInline?: boolean;
  'aria-label'?: string;
  /** Called if the token fetch or HLS attach fails, so the caller can fall back. */
  onError?: () => void;
  /** Label for the graceful-error retry button (R4). i18n at the call site. */
  retryLabel?: string;
}

export function MuxHlsPlayer(props: MuxHlsPlayerProps) {
  // Retry remounts the inner player (fresh `useMuxHls` → failed resets to
  // false and the token fetch re-runs) without any in-effect setState.
  const [attempt, setAttempt] = useState(0);
  return (
    <MuxHlsPlayerInner
      key={attempt}
      {...props}
      onRetry={() => setAttempt((a) => a + 1)}
    />
  );
}

function MuxHlsPlayerInner({
  mediaId,
  className,
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  playsInline = true,
  onError,
  retryLabel = 'Retry',
  onRetry,
  ...rest
}: MuxHlsPlayerProps & { onRetry: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { failed } = useMuxHls(videoRef, mediaId, { autoPlay, onError });

  if (failed) {
    return (
      <div
        className={className}
        role="alert"
        aria-label={rest['aria-label'] ?? 'Video unavailable'}
      >
        <div className="grid h-full w-full place-items-center bg-black/40">
          {/* eslint-disable-next-line kit-components/use-kit-primitives -- bespoke glassmorphic retry control overlaid on the failed-video surface; the shared Button register's fills/shadow don't apply here */}
          <button
            type="button"
            onClick={onRetry}
            className="ring-foreground/20 flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-xs font-medium text-white ring-1 backdrop-blur-sm transition-colors hover:bg-white/20"
          >
            <RotateCw className="size-3.5" />
            {retryLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      className={className}
      muted={muted}
      loop={loop}
      controls={controls}
      playsInline={playsInline}
      preload="metadata"
      aria-label={rest['aria-label']}
    >
      <track kind="captions" />
    </video>
  );
}
